var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { LitElement, css, html } from "lit";
import { property } from "lit/decorators.js";
import { documentCss, documentTest } from "./styles.js";
import { matches, substitute } from "@quandis/qbo4.ui";
export class FileWrapper {
    constructor(file) {
        this.file = file;
        this.id = self.crypto.randomUUID();
        this.progress = 0;
        this.bytesSent = 0;
        this.status = '';
        this.thumbnail = (file.type.startsWith('image'))
            ? URL.createObjectURL(file)
            : null;
    }
    equals(file) {
        return file.name === this.file.name
            && file.size === this.file.size
            && file.lastModified === this.file.lastModified;
    }
}
// Create the map
const uiMap = new Map();
// Add functions to the map
uiMap.set('field', (component) => {
    return html `<input name="" type="text" value="${component.files.length > 0 ? component.files[0].file.name : ''}"/>`;
});
uiMap.set('list', (component) => {
    return html `<ul class="${component.getAttribute('class')}" title="${component.title ?? "Click or drop to upload"}">${component.files.map(wrapper => html `<li>${wrapper.file.name}</li>`)}</ul>`;
});
uiMap.set('table', (component) => {
    return html `<table class="${component.getAttribute('class')}" title="${component.title ?? "Click or drop to upload"}">
        <thead>
        <tr>
            <th>Name</th>
            <th colspan="2">Size</th>
        </tr>
        </thead>
        <tbody>

        ${component.files.map((wrapper, index) => html `<tr>
        <td>${wrapper.file.name}</td>
        <td>${wrapper.file.size}</td>
        <td @click=${(event) => component.removeFile(index, event)}><icon class="bi bi-trash">x</icon></td>
        </th>`)}
        </tbody>
        <tfoot>
            <tr>
                <td colspan="3">Total: ${component.files.length}</td>
            </tr>
        </tfoot>
        </table>`;
});
export class QboFile extends LitElement {
    constructor() {
        super(...arguments);
        this.apiEndpoint = new URL('api://qbo4/documents/files/upload');
        this.provider = null;
        this.path = null;
        this.chunking = true;
        this.forceChunking = true;
        this.chunkSize = 500;
        this.target = '.dropzone';
        this.acceptedFiles = '';
        this.disablePreviews = false;
        this.multiple = false;
        this.accept = '*';
        this.type = '';
        this.files = [];
        this.percent = 0;
        this.queuedClass = 'qbo-queued';
        this.uploadingClass = 'qbo-uploading';
        this.successClass = 'qbo-success';
        this.erroredClass = 'qbo-error';
        this.payload = null;
        // elements that are part of the root DOM / component markup to update with data.
        this.fileInput = null;
        this.fileTemplate = null;
        this.fileLength = null;
        this.fileName = null;
        this.progressBar = null;
        this.dropTarget = this;
        this.clickElement = this;
        this.processing = false;
    }
    static { this.styles = [
        documentCss,
        documentTest,
        css `
.fileInput {
    display: none;
}`
    ]; }
    connectedCallback() {
        super.connectedCallback();
        this.fileInput = this.querySelector('input[type="file"]');
        this.fileTemplate = this.querySelector('template[slot="file"]');
        this.fileName = this.querySelector('*[data-qbo-file-name]');
        this.fileLength = this.querySelector('*[data-qbo-file-length]');
        this.clickElement = this.querySelector('*[data-qbo-file-click]') ?? this;
        this.progressBar = this.querySelector('progress[data-qbo-file-progress]');
        this.dropTarget = this.closest('*[data-qbo-file-drop]') ?? this;
        this.clickElement.addEventListener('click', this.inputClick.bind(this));
        this.dropTarget.addEventListener('drop', this.handleDrop.bind(this));
        this.dropTarget.addEventListener('dragenter', this.handleDragEnter.bind(this));
        this.dropTarget.addEventListener('dragover', this.handleDragOver.bind(this));
        this.dropTarget.addEventListener('dragleave', this.handleDragLeave.bind(this));
    }
    firstUpdated(changedProperties) {
        super.firstUpdated(changedProperties);
        this.fileInput ??= this.shadowRoot?.querySelector('input[type="file"]');
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.dropTarget = this.dropTarget ?? this;
        this.dropTarget.removeEventListener('drop', this.handleDrop);
        this.dropTarget.removeEventListener('dragenter', this.handleDragEnter);
        this.dropTarget.removeEventListener('dragover', this.handleDragOver);
        this.dropTarget.removeEventListener('dragleave', this.handleDragLeave);
        this.clickElement.removeEventListener('click', this.inputClick);
    }
    handleDragEnter(event) {
        event.preventDefault();
        this.dropTarget.classList.add('qbo-drop');
    }
    handleDragOver(event) {
        event.preventDefault();
    }
    handleDragLeave(event) {
        event.preventDefault();
        if (!this.dropTarget.contains(event.relatedTarget) && !this.shadowRoot?.contains(event.relatedTarget)) {
            this.dropTarget.classList.remove('qbo-drop');
        }
    }
    // Define the event handler function for the drop event
    handleDrop(event) {
        event.preventDefault();
        this.dropTarget.classList.remove('qbo-drop');
        if (event.dataTransfer?.files == null)
            return;
        const acceptedTypes = this.accept.split(',').map(type => type.trim());
        // const droppedFiles = event.dataTransfer?.files;
        const droppedFiles = Array.from(event.dataTransfer.files).filter((file) => {
            return acceptedTypes.some(acceptedType => {
                return acceptedType === '*' || file.type === acceptedType || matches(file.type, acceptedType);
            });
        });
        this.dispatchEvent(new CustomEvent('filedrop', {
            detail: {
                files: droppedFiles,
                target: event.target
            },
            bubbles: true,
            composed: true
        }));
        // Process the dropped files
        for (let i = 0; i < droppedFiles.length; i++) {
            this.addFile(droppedFiles[i]);
        }
    }
    addFile(file) {
        if (this.files.find(f => f.equals(file)))
            return;
        const wrapper = new FileWrapper(file);
        wrapper.status = this.queuedClass;
        this.files.push(wrapper);
        this.uploadFile(wrapper);
        this.requestUpdate('files', []);
    }
    removeFile(index, event = null) {
        event?.stopPropagation();
        if (index >= 0 && index < this.files.length) {
            this.files.splice(index, 1);
            this.requestUpdate('files', []);
            if (this.fileInput) {
                this.fileInput.parentElement?.removeChild(this.fileInput);
            }
        }
    }
    inputClick(event) {
        if (event.target?.closest('*[data-qbo-file-remove]') != null) {
            event.stopPropagation();
            event.preventDefault();
            const index = parseInt(event.target.closest('*[data-qbo-index]')?.getAttribute('data-qbo-index') ?? '-1');
            this.removeFile(index);
            return;
        }
        this.fileInput?.click();
    }
    process(event) {
        if (this.fileInput?.files != null) {
            if (!this.multiple) {
                for (let i = this.fileInput?.files?.length - 1; i >= 0; i--) {
                    this.removeFile(i);
                }
            }
            for (let i = 0; i < this.fileInput?.files?.length; i++) {
                this.addFile(this.fileInput.files[i]);
            }
        }
    }
    setValue(element, value) {
        if (element != null) {
            if (element instanceof HTMLInputElement)
                element.value = value;
            else
                element.innerHTML = value;
        }
    }
    updatePercent() {
        const totalSize = this.files.reduce((acc, wrapper) => acc + wrapper.file.size, 0);
        const currentSize = this.files.reduce((acc, wrapper) => acc + wrapper.bytesSent, 0);
        this.percent = (totalSize === 0) ? 0 : Math.round((currentSize / totalSize) * 100);
        if (this.progressBar != null) {
            this.progressBar.setAttribute('value', `${this.percent}`);
            this.progressBar.classList.toggle(this.queuedClass, this.percent === 0);
            this.progressBar.classList.toggle(this.successClass, this.percent >= 100);
            this.progressBar.classList.toggle(this.uploadingClass, this.percent > 0 && this.percent < 100);
        }
        if (this.percent >= 100)
            this.dispatchEvent(new CustomEvent('uploadComplete', { detail: this.files }));
    }
    render() {
        this.updatePercent();
        this.setValue(this.fileLength, this.files.length);
        this.setValue(this.fileName, this.files.length > 0 ? this.files[0].file.name : '');
        if (this.fileTemplate)
            this.replicate(this.fileTemplate);
        const base = uiMap.has(this.type) ? uiMap.get(this.type)(this) : html `<slot></slot>`;
        return html `${base}
        ${this.fileInput instanceof HTMLInputElement ? html `` : html `<input @change="${this.process}" class="fileInput" type="file" tabIndex="-1" ?multiple=${this.multiple} accept="${this.accept}"/>`}
        `;
    }
    replicate(template) {
        template.parentElement?.querySelectorAll('*[data-qbo-index]').forEach(node => node.remove());
        for (let i = 0; i < this.files?.length; i++) {
            const node = template.content.cloneNode(true);
            if (node.firstElementChild != null) {
                node.firstElementChild.setAttribute("data-qbo-index", `${i}`);
                node.firstElementChild.innerHTML = substitute(node.firstElementChild.innerHTML ?? '', this.files[i]);
            }
            template.parentElement?.appendChild(node);
        }
    }
    //substitute(inputString: string, ...jsonData: any): string {
    //    const regex = /\${(.*?)}/g;
    //    let resultString = inputString;
    //    for (let i = 0; i < jsonData.length; i++) {
    //        const data = jsonData[i] as Record<string, any>;
    //        resultString = resultString.replace(regex, (match: string, expression: string) => {
    //            const parts = expression.split(/\.|\[|\]/).filter(part => part !== '');
    //            let currentValue: any = data;
    //            for (let part of parts) {
    //                if (currentValue[part] === undefined)
    //                    return match;
    //                currentValue = currentValue[part];
    //            }
    //            return currentValue;
    //        });
    //    }
    //    resultString = resultString.replace(regex, () => '');
    //    return resultString;
    //}
    async uploadFile(wrapper) {
        const chunkSize = 1024 * this.chunkSize; // 1MB chunk size (adjust as needed)
        let offset = 0;
        let index = 0;
        while (offset < wrapper.file.size) {
            const chunk = wrapper.file.slice(offset, offset + chunkSize);
            const chunkData = await this.readFileAsArrayBuffer(chunk);
            const formData = new FormData();
            formData.append('uuid', wrapper.id);
            formData.append('chunkindex', `${index}`);
            formData.append('totalfilesize', `${wrapper.file.size}`);
            formData.append('chunksize', `${chunkSize}`);
            formData.append('chunkcount', `${Math.ceil(wrapper.file.size / chunkSize)}`);
            formData.append('chunkbyteoffset', `${offset}`);
            formData.append('file', new Blob([chunkData]), wrapper.file.name);
            if (this.provider)
                formData.append('providerName', this.provider);
            if (this.path !== null)
                formData.append('relativePath', `${this.path}${this.path ? '/' : ''}${wrapper.file.name}`);
            if (this.payload != null) {
                for (const key in this.payload) {
                    if (this.payload.hasOwnProperty(key)) {
                        formData.append(key, this.payload[key]);
                    }
                }
            }
            const response = await fetch(this.apiEndpoint, {
                method: 'POST',
                body: formData
            });
            wrapper.status = this.uploadingClass;
            wrapper.bytesSent += chunkData.byteLength;
            wrapper.progress = Math.round((wrapper.bytesSent / wrapper.file.size) * 100);
            if (wrapper.progress > 100)
                wrapper.progress = 100;
            if (response.headers.get('Content-Type')?.includes('application/json'))
                wrapper.response = await response.json();
            if (!response.ok) {
                throw new Error('Upload failed');
            }
            offset += chunkSize;
            index++;
            this.render();
        }
        wrapper.progress = 100;
        wrapper.status = this.successClass;
        this.render();
    }
    async readFileAsArrayBuffer(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                if (reader.result instanceof ArrayBuffer) {
                    resolve(reader.result);
                }
                else {
                    reject(new Error('Failed to read file'));
                }
            };
            reader.onerror = () => {
                reject(reader.error);
            };
            reader.readAsArrayBuffer(file);
        });
    }
}
__decorate([
    property(),
    __metadata("design:type", Object)
], QboFile.prototype, "apiEndpoint", void 0);
__decorate([
    property(),
    __metadata("design:type", Object)
], QboFile.prototype, "provider", void 0);
__decorate([
    property(),
    __metadata("design:type", Object)
], QboFile.prototype, "path", void 0);
__decorate([
    property(),
    __metadata("design:type", Boolean)
], QboFile.prototype, "chunking", void 0);
__decorate([
    property(),
    __metadata("design:type", Boolean)
], QboFile.prototype, "forceChunking", void 0);
__decorate([
    property(),
    __metadata("design:type", Number)
], QboFile.prototype, "chunkSize", void 0);
__decorate([
    property(),
    __metadata("design:type", String)
], QboFile.prototype, "target", void 0);
__decorate([
    property(),
    __metadata("design:type", String)
], QboFile.prototype, "acceptedFiles", void 0);
__decorate([
    property(),
    __metadata("design:type", Boolean)
], QboFile.prototype, "disablePreviews", void 0);
__decorate([
    property(),
    __metadata("design:type", Boolean)
], QboFile.prototype, "multiple", void 0);
__decorate([
    property(),
    __metadata("design:type", String)
], QboFile.prototype, "accept", void 0);
__decorate([
    property(),
    __metadata("design:type", String)
], QboFile.prototype, "type", void 0);
__decorate([
    property({ noAccessor: true }),
    __metadata("design:type", Array)
], QboFile.prototype, "files", void 0);
__decorate([
    property({ noAccessor: true }),
    __metadata("design:type", Number)
], QboFile.prototype, "percent", void 0);
__decorate([
    property(),
    __metadata("design:type", String)
], QboFile.prototype, "queuedClass", void 0);
__decorate([
    property(),
    __metadata("design:type", String)
], QboFile.prototype, "uploadingClass", void 0);
__decorate([
    property(),
    __metadata("design:type", String)
], QboFile.prototype, "successClass", void 0);
__decorate([
    property(),
    __metadata("design:type", String)
], QboFile.prototype, "erroredClass", void 0);
__decorate([
    property({ attribute: false }),
    __metadata("design:type", Object)
], QboFile.prototype, "payload", void 0);
if (!customElements.get('qbo-file'))
    customElements.define('qbo-file', QboFile);
